import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Form,
  Link,
  useActionData,
  useNavigate,
  useNavigation,
} from 'react-router-dom'
import { SignInActionData, SignInActionError } from '../../actions/signInAction'
import Button from '../../components/Button'
import Dialog from '../../components/Dialog'
import Input from '../../components/Input'
import PageLayout from '../../components/PageLayout'
import PasswordInput from '../../components/PasswordInput'
import SocialAuthButtons from '../../components/SocialAuthButtons'
import { getPostSignInPath } from '../../helpers/getPostSignInPath'
import useSocialAuth from '../../hooks/useSocialAuth'

export default function SignInPage() {
  const navigation = useNavigation()
  const navigate = useNavigate()
  const signInActionData = useActionData() as SignInActionData
  const { t } = useTranslation()

  const [errorDialogOpen, setErrorDialogOpen] = useState(false)
  const error = signInActionData?.error

  const isSubmitting = ['loading', 'submitting'].includes(navigation.state)

  const handleSocialSuccess = useCallback(
    async (userId: string | undefined) => {
      const pathFromQuery = new URLSearchParams(window.location.search).get(
        'path',
      )
      const path = await getPostSignInPath({ pathFromQuery, userId })
      navigate(path)
    },
    [navigate],
  )

  const {
    busyProvider,
    clearError: clearSocialError,
    error: socialError,
    onPressApple,
    onPressGoogle,
  } = useSocialAuth({ onSuccess: handleSocialSuccess })

  const translateError = (error: SignInActionError) => {
    switch (error.code) {
      case 'invalid_credentials':
        return t('pages.account.signIn.invalidCredentials')
      case 'validation_failed':
        return t('pages.account.signIn.invalidEmail')
      default:
        return error.message
    }
  }

  // Password action-data is sticky across navigations; drop any prior social
  // message as soon as the user submits credentials so the dialog shows the
  // latest failure (and forgot-password stays available for credential errors).
  useEffect(() => {
    if (isSubmitting) {
      clearSocialError()
    }
  }, [clearSocialError, isSubmitting])

  useEffect(() => {
    if (error) {
      setErrorDialogOpen(true)
    }
  }, [error])

  useEffect(() => {
    if (socialError) {
      setErrorDialogOpen(true)
    }
  }, [socialError])

  // Prefer a live social failure over sticky password action-data so a later
  // Apple/Google error is not masked by an earlier credentials dialog.
  const dialogMessage = socialError
    ? socialError
    : error
      ? translateError(error)
      : undefined
  const showForgotPasswordInDialog = Boolean(error) && !socialError

  return (
    <PageLayout>
      <div className="flex grow flex-col space-y-11">
        <div className="shrink-0 space-y-6 text-center">
          <h1 className="custom-heading">
            {t('pages.account.signIn.heading')}
          </h1>

          {dialogMessage && (
            <Dialog
              onClose={() => setErrorDialogOpen(false)}
              open={errorDialogOpen}
            >
              <p className="text-center text-[30px]">❌</p>
              <p className="mb-6 text-center text-lg font-bold">
                {dialogMessage}
              </p>

              {showForgotPasswordInDialog ? (
                <Button
                  dataTestId="button-forgot"
                  onClick={() => navigate('/account/forgot-password')}
                  title={t('pages.account.signIn.forgotPassword')}
                  type="button"
                />
              ) : null}
            </Dialog>
          )}
        </div>

        <div className="flex grow flex-col justify-center space-y-7">
          <SocialAuthButtons
            busyProvider={busyProvider}
            onPressApple={onPressApple}
            onPressGoogle={onPressGoogle}
            showDividerBelow
          />

          <Form className="space-y-7" method="post">
            <fieldset
              className="space-y-7"
              disabled={isSubmitting || busyProvider !== null}
            >
              <Input
                autoComplete="email"
                bgColor={'#FFFFFF'}
                dataTestId="textfield-email"
                name="email"
                placeholder={t('shared.email')}
                type="email"
              />

              <PasswordInput
                autoComplete="current-password"
                bgColor={'#FFFFFF'}
                name="password"
              />

              <Link
                className="float-right mr-4 text-sm font-bold text-[#A2A4AE] underline"
                data-testid="button-forgot"
                to="/account/forgot-password"
              >
                {t('pages.account.signIn.forgotPassword')}
              </Link>
            </fieldset>

            <Button
              dataTestId="button-continue-credentials"
              disabled={busyProvider !== null}
              submitting={isSubmitting}
              title={t('shared.signIn')}
              type="submit"
            />
          </Form>
        </div>

        <div className="shrink-0 text-center">
          <Link className="text-lg font-bold" to="/onboarding">
            {t('pages.account.signIn.signUp')}
          </Link>
        </div>
      </div>
    </PageLayout>
  )
}
